<ion-content class="shop-content"
             [style.--background]="storefront?.event?.backgroundURI ? 'url(' + storefront?.event?.backgroundURI + ') no-repeat center center / cover' : null">
  <main class="shop-shell">
    <section *ngIf="isLoading" class="state-card" aria-live="polite">
      <ion-spinner name="crescent"></ion-spinner>
      <p>{{ 'SHOP.LOADING' | translate }}</p>
    </section>

    <section *ngIf="!isLoading && errorMessage && !storefront" class="state-card">
      <ion-icon name="storefront-outline"></ion-icon>
      <h1>{{ 'SHOP.UNAVAILABLE' | translate }}</h1>
      <p>{{ errorMessage }}</p>
    </section>

    <ng-container *ngIf="!isLoading && storefront">
      <section class="event-presentation">
        <div *ngIf="storefront.event.bannerURI" class="event-banner">
          <img [src]="storefront.event.bannerURI" [alt]="storefront.event.name">
        </div>
        <header class="shop-header">
          <div>
            <p class="organizer">{{ storefront.organizer.commercialName }}</p>
            <h1>{{ storefront.event.name }}</h1>
            <p *ngIf="localize(storefront.event.description)" class="event-description">{{ localize(storefront.event.description) }}</p>
          </div>
          <label *ngIf="storefront.event.communicationLanguages.length > 1" class="language-select">
            <span class="sr-only">Language</span>
            <select [value]="selectedLanguage" (change)="setLanguage($any($event.target).value)">
              <option *ngFor="let language of storefront.event.communicationLanguages" [value]="language">{{ language | uppercase }}</option>
            </select>
          </label>
        </header>
      </section>

      <ng-container *ngIf="step === 'catalogue'">
        <section *ngIf="storefront.shops.length === 0" class="state-card">
          <ion-icon name="storefront-outline"></ion-icon>
          <h2>{{ 'SHOP.NO_SHOPS' | translate }}</h2>
        </section>

        <ion-segment *ngIf="!storefront.isSingleShop && storefront.shops.length > 1"
                     class="shop-segments"
                     [scrollable]="true"
                     [value]="selectedShopId"
                     (ionChange)="selectShop($event)">
          <ion-segment-button *ngFor="let shop of storefront.shops; trackBy: trackById" [value]="shop.id">
            <ion-label>{{ localize(shop.label) }}</ion-label>
          </ion-segment-button>
        </ion-segment>

        <section *ngIf="selectedShop as shop" class="catalogue">
          <div class="shop-introduction">
            <h2>{{ localize(shop.label) }}</h2>
            <p *ngIf="localize(shop.description)">{{ localize(shop.description) }}</p>
          </div>

          <section *ngFor="let group of getGroups(shop); trackBy: trackById" class="item-group">
            <header>
              <h3>{{ localize(group.label) }}</h3>
              <p *ngIf="localize(group.description)">{{ localize(group.description) }}</p>
            </header>

            <ion-list lines="full">
              <ion-item *ngFor="let item of getItems(shop, group.id); trackBy: trackById" class="product-row">
                <ion-label class="ion-text-wrap">
                  <h4>{{ localize(item.label) }}</h4>
                  <p *ngIf="localize(item.description)" class="item-description">{{ localize(item.description) }}</p>
                  <div class="price-line">
                    <strong>{{ formatAmount(item.price) }}</strong>
                    <span *ngIf="item.depositAmount > 0">+ {{ formatAmount(item.depositAmount) }} {{ 'SHOP.DEPOSIT' | translate }}</span>
                  </div>
                  <ion-note *ngIf="item.remaining === 0" color="danger">{{ 'SHOP.SOLD_OUT' | translate }}</ion-note>
                </ion-label>

                <div class="quantity-control" slot="end" [attr.aria-label]="localize(item.label)">
                  <ion-button fill="clear" size="small" [disabled]="getQuantity(item) === 0" (click)="changeQuantity(item, -1)" [attr.aria-label]="'SHOP.REMOVE' | translate">
                    <ion-icon slot="icon-only" name="remove-outline"></ion-icon>
                  </ion-button>
                  <span aria-live="polite">{{ getQuantity(item) }}</span>
                  <ion-button fill="clear" size="small" [disabled]="!canIncrement(item)" (click)="changeQuantity(item, 1)" [attr.aria-label]="'SHOP.ADD' | translate">
                    <ion-icon slot="icon-only" name="add-outline"></ion-icon>
                  </ion-button>
                </div>
              </ion-item>
            </ion-list>
          </section>
        </section>

        <div *ngIf="cartQuantity > 0" class="cart-bar">
          <div>
            <strong>{{ cartQuantity }} {{ 'SHOP.ITEMS' | translate }}</strong>
            <span>{{ formatAmount(cartTotal) }}</span>
          </div>
          <ion-button (click)="openCheckout()">
            <ion-icon slot="start" name="cart-outline"></ion-icon>
            {{ 'SHOP.VIEW_CART' | translate }}
          </ion-button>
        </div>
      </ng-container>

      <section *ngIf="step === 'checkout'" class="checkout-card">
        <button type="button" class="back-button" (click)="returnToCatalogue()">
          <ion-icon name="arrow-back-outline"></ion-icon>
          {{ 'SHOP.BACK' | translate }}
        </button>
        <h2>{{ 'SHOP.ORDER_SUMMARY' | translate }}</h2>

        <ion-list lines="full" class="summary-list">
          <ion-item *ngFor="let line of cartLines; trackBy: trackByCartLine">
            <ion-label class="ion-text-wrap">
              <h3>{{ line.quantity }} × {{ localize(line.item.label) }}</h3>
              <p>{{ localize(line.shop.label) }}</p>
            </ion-label>
            <strong slot="end">{{ formatAmount((line.item.price + line.item.depositAmount) * line.quantity) }}</strong>
          </ion-item>
        </ion-list>

        <div class="totals">
          <p><span>{{ 'SHOP.PRODUCTS' | translate }}</span><strong>{{ formatAmount(productSubtotal) }}</strong></p>
          <p *ngIf="depositSubtotal > 0"><span>{{ 'SHOP.DEPOSITS' | translate }}</span><strong>{{ formatAmount(depositSubtotal) }}</strong></p>
          <p class="grand-total"><span>{{ 'SHOP.TOTAL' | translate }}</span><strong>{{ formatAmount(cartTotal) }}</strong></p>
        </div>

        <form [formGroup]="checkoutForm" (ngSubmit)="submitOrder()" novalidate>
          <h2>{{ 'SHOP.CUSTOMER_DETAILS' | translate }}</h2>
          <ion-list lines="full">
            <ion-item>
              <ion-input formControlName="firstName" [label]="'SHOP.FIRST_NAME' | translate" labelPlacement="stacked" autocomplete="given-name"></ion-input>
            </ion-item>
            <ion-item>
              <ion-input formControlName="lastName" [label]="'SHOP.LAST_NAME' | translate" labelPlacement="stacked" autocomplete="family-name"></ion-input>
            </ion-item>
            <ion-item>
              <ion-input formControlName="email" type="email" [label]="'SHOP.EMAIL' | translate" labelPlacement="stacked" autocomplete="email"></ion-input>
            </ion-item>
            <ion-item>
              <ion-input formControlName="emailConfirmation"
                         type="email"
                         [label]="'SHOP.CONFIRM_EMAIL' | translate"
                         labelPlacement="stacked"
                         autocomplete="new-password"
                         (paste)="preventEmailConfirmationPaste($event)"></ion-input>
            </ion-item>
          </ion-list>
          <p class="email-confirmation-note">{{ 'SHOP.EMAIL_CONFIRMATION_HELP' | translate }}</p>
          <p *ngIf="checkoutForm.hasError('emailMismatch') && checkoutForm.get('emailConfirmation')?.touched" class="form-error">
            {{ 'SHOP.EMAIL_MISMATCH' | translate }}
          </p>

          <p *ngIf="errorMessage" class="form-error">{{ errorMessage }}</p>
          <ion-button type="submit" expand="block" size="large" [disabled]="checkoutForm.invalid || isSubmitting">
            <span>{{ 'SHOP.PAY' | translate }} {{ formatAmount(cartTotal) }}</span>
          </ion-button>
          <p class="secure-note">{{ 'SHOP.SECURE_PAYMENT' | translate }}</p>
        </form>
      </section>

      <section *ngIf="step === 'payment'" class="state-card" aria-live="polite">
        <ion-spinner name="crescent"></ion-spinner>
        <h2>{{ (isSubmitting ? 'SHOP.PREPARING_PAYMENT' : 'SHOP.CHECKING_PAYMENT') | translate }}</h2>
        <p>{{ (isSubmitting ? 'SHOP.PREPARING_PAYMENT_CONTENT' : 'SHOP.CHECKING_PAYMENT_CONTENT') | translate }}</p>
      </section>

      <section *ngIf="step === 'result'" class="state-card result-card" aria-live="polite">
        <ion-icon *ngIf="orderStatus?.status === 'paid'" class="success-icon" name="checkmark-circle-outline"></ion-icon>
        <h2 *ngIf="orderStatus?.status === 'paid'">{{ 'SHOP.PAID_TITLE' | translate }}</h2>
        <p *ngIf="orderStatus?.status === 'paid'">{{ 'SHOP.PAID_CONTENT' | translate }}</p>

        <h2 *ngIf="orderStatus?.status === 'cancelled'">{{ 'SHOP.CANCELLED_TITLE' | translate }}</h2>
        <p *ngIf="orderStatus?.status === 'cancelled'">{{ 'SHOP.CANCELLED_CONTENT' | translate }}</p>

        <h2 *ngIf="orderStatus?.status === 'expired'">{{ 'SHOP.EXPIRED_TITLE' | translate }}</h2>
        <p *ngIf="orderStatus?.status === 'expired'">{{ 'SHOP.EXPIRED_CONTENT' | translate }}</p>

        <h2 *ngIf="orderStatus && ['reserved', 'payment_pending'].includes(orderStatus.status)">{{ 'SHOP.PENDING_TITLE' | translate }}</h2>
        <p *ngIf="orderStatus && ['reserved', 'payment_pending'].includes(orderStatus.status)">{{ 'SHOP.PENDING_CONTENT' | translate }}</p>

        <div *ngIf="orderStatus?.status === 'paid'" class="pickup-summary">
          <h3>{{ 'SHOP.PICKUPS' | translate }}</h3>
          <p *ngFor="let shop of orderStatus?.shops">{{ localize(shop.label) }} · {{ formatAmount(shop.total) }}</p>
        </div>

        <p *ngIf="errorMessage" class="form-error">{{ errorMessage }}</p>
        <ion-button *ngIf="orderStatus && ['reserved', 'payment_pending'].includes(orderStatus.status)" fill="outline" [disabled]="isCheckingPayment" (click)="refreshPaymentStatus()">
          <ion-icon slot="start" name="refresh-outline"></ion-icon>
          {{ 'SHOP.RETRY' | translate }}
        </ion-button>
        <ion-button *ngIf="orderStatus && ['paid', 'cancelled', 'expired'].includes(orderStatus.status)" fill="outline" (click)="startNewOrder()">
          {{ 'SHOP.NEW_ORDER' | translate }}
        </ion-button>
      </section>

      <footer class="shop-footer">
        <a *ngIf="storefront.organizer.termsAndConditionsURL" [href]="storefront.organizer.termsAndConditionsURL" target="_blank" rel="noopener">{{ 'GENERIC.TERMS' | translate }}</a>
        <a *ngIf="storefront.organizer.privacyPolicyURL" [href]="storefront.organizer.privacyPolicyURL" target="_blank" rel="noopener">{{ 'GENERIC.PRIVACY' | translate }}</a>
        <span>{{ 'SHOP.POWERED_BY' | translate }} Welkom</span>
      </footer>
    </ng-container>
  </main>

</ion-content>
